/**
 * Tags in the master lists of the Single App and Sync Group editors: the chips
 * under a row and the filter above the list (measures and dimensions only;
 * variables carry no tags).
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge, MultiSelect, type SelectOption } from "@h2j-data-team/ui";
import { pickerMessages } from "@/lib/ui-labels";

const MAX_ROW_TAGS = 2;

/** Up to two chips, filtered tags first, then "+N"; every tag in the tooltip. */
export function RowTags({
  tags,
  selected,
}: {
  tags: readonly string[];
  selected: readonly string[];
}) {
  const { t } = useTranslation("common");
  if (tags.length === 0) return null;
  const ordered = [...tags].sort((a, b) => +selected.includes(b) - +selected.includes(a));
  return (
    <span className="mt-1 flex items-center gap-1 overflow-hidden" title={tags.join(", ")}>
      {ordered.slice(0, MAX_ROW_TAGS).map((tag) => (
        <Badge key={tag} variant="neutral" className="min-w-0 max-w-40 shrink">
          <span className="truncate">{tag}</span>
        </Badge>
      ))}
      {tags.length > MAX_ROW_TAGS && (
        <span className="shrink-0 font-mono text-[11px] text-faint">
          {t("list.more", { count: tags.length - MAX_ROW_TAGS })}
        </span>
      )}
    </span>
  );
}

/** Tag picker built from the tab's rows, each option with its row count. */
export function TagFilter({
  tagLists,
  value,
  onValueChange,
}: {
  tagLists: readonly (readonly string[])[];
  value: string[];
  onValueChange: (value: string[]) => void;
}) {
  const { t } = useTranslation("common");
  const options = useMemo<SelectOption[]>(() => {
    const counts = new Map<string, number>();
    for (const tags of tagLists) {
      for (const tag of tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
    return [...counts]
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map(([tag, count]) => ({ value: tag, label: tag, hint: String(count) }));
  }, [tagLists]);

  if (options.length === 0 && value.length === 0) return null;
  return (
    <MultiSelect<string>
      aria-label={t("list.filterByTag")}
      size="compact"
      className="w-full"
      options={options}
      value={value}
      onValueChange={onValueChange}
      messages={pickerMessages({ placeholder: t("list.filterByTag") })}
    />
  );
}
